import { restSafe, restUnsafe } from '../dungeon/roomRules';
import type { ActionConsequence, DungeonFloor, PlayerState } from '../game/types';
import { addItem } from '../inventory/inventory';
import { grantExp, learnSkill } from '../progress/progression';

export interface ConsequenceContext {
  floor: DungeonFloor;
  player: PlayerState;
  currentRoomId: string;
  previousRoomId: string | null;
  action?: string;
}

export interface ConsequenceResult {
  floor: DungeonFloor;
  player: PlayerState;
  previousRoomId: string | null;
  message: string;
  triggerCombat: boolean;
  enemyKey: string | null;
  retreatApplied: boolean;
  nextFloor: number | null;
  killed: boolean;
  failed: boolean;
}

function failedConsequence(
  context: Pick<ConsequenceContext, 'floor' | 'player' | 'previousRoomId'>,
  message: string,
): ConsequenceResult {
  return {
    ...context,
    message,
    triggerCombat: false,
    enemyKey: null,
    retreatApplied: false,
    nextFloor: null,
    killed: false,
    failed: true,
  };
}

function spendMana(player: PlayerState, amount: number): PlayerState | null {
  return amount < 0 || player.mana < amount
    ? null
    : { ...player, mana: player.mana - amount };
}

function markRoomCleared(floor: DungeonFloor, roomId: string): DungeonFloor {
  const room = floor.rooms[roomId];
  return room
    ? {
        ...floor,
        rooms: { ...floor.rooms, [roomId]: { ...room, isCleared: true } },
      }
    : floor;
}

function applyRewards(
  player: PlayerState,
  consequence: ActionConsequence,
  initialMessage: string,
): { player: PlayerState; message: string } {
  let next = consequence.gainGold
    ? { ...player, gold: player.gold + consequence.gainGold }
    : player;
  let message = initialMessage;

  if (consequence.learnSkillId) {
    const learning = learnSkill(next, consequence.learnSkillId);
    if (learning.ok) {
      next = learning.player;
      message = `${message} Học được ${learning.skillName}.`;
    } else {
      message = `${message} ${learning.message}`;
    }
  }
  if (consequence.grantItemId) {
    next = {
      ...next,
      inventory: addItem(
        next.inventory,
        consequence.grantItemId,
        consequence.grantItemQty ?? 1,
      ),
    };
  }
  if (consequence.grantExp) {
    const gained = grantExp(next, consequence.grantExp);
    next = gained.player;
    if (gained.messages.length > 0) {
      message = `${message} ${gained.messages.join(' ')}`;
    }
  }
  return { player: next, message };
}

/** Apply one data-driven room option. Combat victory clears rooms elsewhere. */
export function applyConsequence(
  context: ConsequenceContext,
  consequence: ActionConsequence,
): ConsequenceResult {
  let { floor, player, previousRoomId } = context;
  const { currentRoomId, action } = context;
  let triggerCombat = Boolean(consequence.triggerCombat);
  let retreatApplied = false;
  let killed = false;
  let message = consequence.message;

  if (consequence.learnSkillId && action === 'LEARN_SKILL') {
    const learning = learnSkill(player, consequence.learnSkillId);
    if (!learning.ok) {
      return failedConsequence({ floor, player, previousRoomId }, learning.message);
    }
  }

  if (consequence.spendMana && consequence.spendMana > 0) {
    const spent = spendMana(player, consequence.spendMana);
    if (!spent) {
      return failedConsequence(
        { floor, player, previousRoomId },
        'Không đủ Ma Lực để thực hiện hành động này.',
      );
    }
    player = spent;
  }

  if (consequence.loseGold) {
    if (player.gold < consequence.loseGold) {
      return failedConsequence({ floor, player, previousRoomId }, 'Không đủ vàng.');
    }
    player = { ...player, gold: player.gold - consequence.loseGold };
  }

  if (action === 'REST_SAFE') {
    player = restSafe(player);
  } else if (action === 'REST_UNSAFE') {
    player = restUnsafe(player);
    triggerCombat = true;
  } else if (consequence.healMpPercent !== undefined) {
    if (consequence.healMpPercent >= 100) {
      player = restSafe(player);
    } else {
      const recovered = Math.floor(player.maxMana * (consequence.healMpPercent / 100));
      player = { ...player, mana: Math.min(player.maxMana, player.mana + recovered) };
    }
  }

  ({ player, message } = applyRewards(player, consequence, message));

  if (consequence.damageHp && consequence.damageHp > 0) {
    player = { ...player, hp: 0, alive: false };
    killed = true;
  }
  if (consequence.setCleared && !consequence.triggerCombat && action !== 'REST_UNSAFE') {
    floor = markRoomCleared(floor, currentRoomId);
  }
  if (consequence.retreatToPrevious && previousRoomId && floor.rooms[previousRoomId]) {
    floor = { ...floor, playerRoomId: previousRoomId };
    retreatApplied = true;
  }

  const enemyKey =
    consequence.enemyId ??
    consequence.bossName ??
    consequence.monsterName ??
    (triggerCombat ? 'ambusher' : null);

  return {
    floor,
    player,
    previousRoomId: retreatApplied ? currentRoomId : previousRoomId,
    message,
    triggerCombat: triggerCombat && !killed,
    enemyKey,
    retreatApplied,
    nextFloor: consequence.nextFloor ?? null,
    killed,
    failed: false,
  };
}

export function clearRoom(floor: DungeonFloor, roomId: string): DungeonFloor {
  return markRoomCleared(floor, roomId);
}
